import { gsap } from "gsap";
import { MorphSVGPlugin } from "gsap/MorphSVGPlugin";

gsap.registerPlugin(MorphSVGPlugin);

export function initPolygons() {
  /*
  Shape schema:
  {
    "vertices": [
      {
        "normal": { "x": 10, "y": 10 },
        "hover": { "x": 12, "y": 8 }
      },
      ...
    ],
    "label": {
      "text": "ABBR",
      "vertex": 0,
      "position": "above" // "above", "below", "left", "right"
    }
  }
  */
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const lineStroke = '#FFFFFF';
  const lineOpacity = 0.3;
  const BOX_SIZE = 5;
  const BOX_HALF = BOX_SIZE / 2;
  const boxStroke = '#363638';
  const boxStrokeHover = '#FFFFFF';
  const boxFill = '#000000';
  const boxFillHover = '#FFFFFF';
  const labelFill = '#8b8f94';
  const labelFillHover = '#FFFFFF';

  const toPoints = (pts) => pts.map(p => `${p.x},${p.y}`).join(' ');

  function labelOffset(pt, position) {
    const OFFSET = 8;
    switch (position) {
      case 'above': return { x: pt.x, y: pt.y - OFFSET, anchor: 'middle', baseline: 'auto' };
      case 'below': return { x: pt.x, y: pt.y + OFFSET, anchor: 'middle', baseline: 'hanging' };
      case 'left': return { x: pt.x - BOX_HALF - 4, y: pt.y, anchor: 'end', baseline: 'middle' };
      case 'right': return { x: pt.x + BOX_HALF + 4, y: pt.y, anchor: 'start', baseline: 'middle' };
    }
  }

  const shapeDescriptions = document.querySelectorAll('.block-solutions-grid__shape-description-json');

  shapeDescriptions.forEach(desc => {
    const shapeJson = desc.dataset.shape;
    if (!shapeJson) return;

    let shape;
    try {
      shape = JSON.parse(shapeJson);
    } catch (e) {
      console.error('Invalid JSON in shape description:', e);
      return;
    }

    const vertices = shape.vertices;
    const label = shape.label;
    if (!vertices || vertices.length < 3) return;

    const normalPts = vertices.map(v => v.normal);
    const hoverPts = vertices.map(v => v.hover);

    // Viewbox
    const viewboxX = 320;
    const viewboxY = 150;

    // Build SVG
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', `0 0 ${viewboxX} ${viewboxY}`);
    svg.setAttribute('xmlns', SVG_NS);
    // svg.style.cssText = 'width:100%;height:100%;overflow:visible;';

    // Closed polygon outline
    const polygon = document.createElementNS(SVG_NS, 'polygon');
    polygon.setAttribute('points', toPoints(normalPts));
    polygon.setAttribute('fill', 'none');
    polygon.setAttribute('stroke', lineStroke);
    polygon.setAttribute('stroke-width', '1');
    polygon.setAttribute('opacity', lineOpacity);
    svg.appendChild(polygon);

    // Label
    const labelVertexIdx = label.vertex;
    const labelPosNormal = labelOffset(normalPts[labelVertexIdx], label.position);
    const labelPosHover = labelOffset(hoverPts[labelVertexIdx], label.position);

    const textEl = document.createElementNS(SVG_NS, 'text');
    textEl.setAttribute('x', labelPosNormal.x);
    textEl.setAttribute('y', labelPosNormal.y);
    textEl.setAttribute('text-anchor', labelPosNormal.anchor);
    textEl.setAttribute('dominant-baseline', labelPosNormal.baseline);
    textEl.setAttribute('fill', labelFill);
    textEl.setAttribute('font-size', '0.75rem');
    textEl.setAttribute('font-family', 'IBM Plex Mono, monospace');
    textEl.setAttribute('letter-spacing', '0.5');
    textEl.textContent = label.text;
    svg.appendChild(textEl);

    const labelEl = {
      el: textEl,
      xNormal: labelPosNormal.x,
      yNormal: labelPosNormal.y,
      xHover: labelPosHover.x,
      yHover: labelPosHover.y
    };

    // Small square boxes at each vertex
    const boxEls = normalPts.map((pt, i) => {
      const normalX = pt.x - BOX_HALF;
      const normalY = pt.y - BOX_HALF;
      const rect = document.createElementNS(SVG_NS, 'rect');
      rect.setAttribute('width', BOX_SIZE);
      rect.setAttribute('height', BOX_SIZE);
      rect.setAttribute('x', normalX);
      rect.setAttribute('y', normalY);
      rect.setAttribute('fill', boxFill);
      rect.setAttribute('stroke', boxStroke);
      rect.setAttribute('stroke-width', '1');
      svg.appendChild(rect);

      const isLabel = i === labelVertexIdx;
      return {
        el: rect,
        xNormal: normalX,
        yNormal: normalY,
        xHover: hoverPts[i].x - BOX_HALF,
        yHover: hoverPts[i].y - BOX_HALF,
        isLabel: isLabel,
      };
    });

    // Insert the built SVG right after the (hidden) data element
    desc.insertAdjacentElement('afterend', svg);

    // Hover animation on pointer devices only
    if (window.matchMedia('(hover: hover)').matches) {
      var card = desc.closest('.block-solutions-grid__card');
      var target = card || desc.parentElement;
      var normalPoints = toPoints(normalPts);
      var hoverPoints = toPoints(hoverPts);
      var tl = null;

      target.addEventListener('mouseenter', () => {
        if (tl) { tl.kill(); tl = null; }
        tl = gsap.timeline();

        tl.to(polygon, { attr: { points: hoverPoints }, duration: 0.6, ease: 'power2.out' }, 0);

        boxEls.forEach(b => {
          const stroke = b.isLabel ? boxStrokeHover : boxStroke;
          const fill = b.isLabel ? boxFillHover : boxFill;
          tl.to(b.el, {
            attr: { x: b.xHover, y: b.yHover, stroke, fill },
            duration: 0.6, ease: 'power2.out'
          }, 0);
        });

        tl.to(labelEl.el, {
          attr: { x: labelEl.xHover, y: labelEl.yHover, fill: labelFillHover },
          duration: 0.6, ease: 'power2.out'
        }, 0);
      });

      target.addEventListener('mouseleave', () => {
        if (tl) { tl.kill(); tl = null; }
        tl = gsap.timeline();

        tl.to(polygon, { attr: { points: normalPoints }, duration: 0.6, ease: 'power2.inOut' }, 0);

        boxEls.forEach(b => {
          tl.to(b.el, {
            attr: { x: b.xNormal, y: b.yNormal, stroke: boxStroke, fill: boxFill },
            duration: 0.6, ease: 'power2.inOut'
          }, 0);
        });

        if (labelEl) {
          tl.to(labelEl.el, {
            attr: { x: labelEl.xNormal, y: labelEl.yNormal, fill: labelFill },
            duration: 0.6, ease: 'power2.inOut'
          }, 0);
        }
      });
    }
  });
}

/**
 * Morph Animation (GSAP MorphSVGPlugin)
 * Cards with data-morph: extracts line elements from Shape A and Shape B SVGs,
 * renders Shape A inline, morphs lines A→B on hover, B→A on leave.
 * Small square boxes are dynamically created at each vertex and animated
 * on the same timeline so they track line endpoints precisely.
 *
 * Optional hover media: if data-hover-media and data-hover-src are set on the
 * wrapper, a <foreignObject> with the image/video is inserted into the SVG
 * behind the lines, clipped to the polygon shape via an SVG <clipPath>.
 * The clip polygon animates with the morph and the media fades in on hover.
 */
export function initMorph() {
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var XHTML_NS = 'http://www.w3.org/1999/xhtml';
  var BOX_SIZE = 5;
  var BOX_HALF = BOX_SIZE / 2;
  var morphId = 0; // unique ID counter for clipPaths
  var wrappers = document.querySelectorAll('.block-solutions-grid__illustration[data-morph]');

  /**
   * Extract unique vertex coordinates from <line> elements.
   * Returns an array of {x, y} in the order they appear (deduped).
   */
  function extractVertices(lines) {
    var seen = {};
    var verts = [];
    for (var i = 0; i < lines.length; i++) {
      var coords = [
        { x: parseFloat(lines[i].getAttribute('x1')), y: parseFloat(lines[i].getAttribute('y1')) },
        { x: parseFloat(lines[i].getAttribute('x2')), y: parseFloat(lines[i].getAttribute('y2')) }
      ];
      coords.forEach(function(c) {
        var key = c.x.toFixed(4) + ',' + c.y.toFixed(4);
        if (!seen[key]) {
          seen[key] = true;
          verts.push(c);
        }
      });
    }
    return verts;
  }

  /**
   * Build an SVG points string from vertex array: "x1,y1 x2,y2 ..."
   */
  function vertsToPoints(verts) {
    return verts.map(function(v) { return v.x + ',' + v.y; }).join(' ');
  }

  wrappers.forEach(function(wrapper) {
    var card = wrapper.closest('.block-solutions-grid__card');
    if (!card) return;

    var srcA = wrapper.querySelector('.block-solutions-grid__morph-a');
    var srcB = wrapper.querySelector('.block-solutions-grid__morph-b');
    if (!srcA || !srcB) return;

    var svgA = srcA.querySelector('svg');
    var svgB = srcB.querySelector('svg');
    if (!svgA || !svgB) return;

    // Grab original <line> elements BEFORE conversion to extract vertices.
    var linesAOrig = svgA.querySelectorAll('line');
    var linesBOrig = svgB.querySelectorAll('line');
    var vertsA = extractVertices(linesAOrig);
    var vertsB = extractVertices(linesBOrig);

    // Remove any <rect> elements from both SVGs — JS creates them dynamically.
    svgA.querySelectorAll('rect').forEach(function(r) { r.remove(); });
    svgB.querySelectorAll('rect').forEach(function(r) { r.remove(); });

    // Convert remaining non-path elements (lines) to <path>
    MorphSVGPlugin.convertToPath(svgA.querySelectorAll('circle, ellipse, line, polyline, polygon'));
    MorphSVGPlugin.convertToPath(svgB.querySelectorAll('circle, ellipse, line, polyline, polygon'));

    var pathsA = svgA.querySelectorAll('path');
    var pathsB = svgB.querySelectorAll('path');

    if (!pathsA.length || !pathsB.length) return;

    // Clone SVG A as the visible illustration and display it.
    var liveSvg = svgA.cloneNode(true);
    liveSvg.querySelectorAll('rect').forEach(function(r) { r.remove(); });
    MorphSVGPlugin.convertToPath(liveSvg.querySelectorAll('circle, ellipse, line, polyline, polygon'));

    // Compute a combined viewBox that fits both A and B shapes so
    // morphing to B's coordinates won't be clipped by A's viewBox.
    var vbA = (svgA.getAttribute('viewBox') || '').split(/\s+/).map(Number);
    var vbB = (svgB.getAttribute('viewBox') || '').split(/\s+/).map(Number);
    var mergedVb = vbA;
    if (vbA.length === 4 && vbB.length === 4) {
      var minX = Math.min(vbA[0], vbB[0]);
      var minY = Math.min(vbA[1], vbB[1]);
      var maxX = Math.max(vbA[0] + vbA[2], vbB[0] + vbB[2]);
      var maxY = Math.max(vbA[1] + vbA[3], vbB[1] + vbB[3]);
      mergedVb = [minX, minY, maxX - minX, maxY - minY];
      liveSvg.setAttribute('viewBox', mergedVb.join(' '));
    }

    // --- Hover media: image or video clipped to the polygon shape ---
    var hoverMediaType = wrapper.getAttribute('data-hover-media');
    var hoverSrc = wrapper.getAttribute('data-hover-src');
    var clipPolygon = null;  // SVG <polygon> inside <clipPath>
    var mediaFoEl = null;    // the <foreignObject> wrapping the media

    if (hoverMediaType && hoverSrc && vertsA.length >= 3) {
      var clipId = 'morph-clip-' + morphId++;

      // Create <defs><clipPath><polygon/></clipPath></defs>
      var defs = document.createElementNS(SVG_NS, 'defs');
      var clipPath = document.createElementNS(SVG_NS, 'clipPath');
      clipPath.setAttribute('id', clipId);
      clipPolygon = document.createElementNS(SVG_NS, 'polygon');
      clipPolygon.setAttribute('points', vertsToPoints(vertsA));
      clipPath.appendChild(clipPolygon);
      defs.appendChild(clipPath);
      liveSvg.insertBefore(defs, liveSvg.firstChild);

      // Create <foreignObject> covering the full viewBox, clipped
      var fo = document.createElementNS(SVG_NS, 'foreignObject');
      fo.setAttribute('x', mergedVb[0]);
      fo.setAttribute('y', mergedVb[1]);
      fo.setAttribute('width', mergedVb[2]);
      fo.setAttribute('height', mergedVb[3]);
      fo.setAttribute('clip-path', 'url(#' + clipId + ')');
      fo.style.opacity = '0';

      if (hoverMediaType === 'video') {
        var video = document.createElementNS(XHTML_NS, 'video');
        video.setAttribute('src', hoverSrc);
        video.setAttribute('muted', '');
        video.setAttribute('loop', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('preload', 'auto');
        video.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';
        fo.appendChild(video);
      } else {
        var img = document.createElementNS(XHTML_NS, 'img');
        img.setAttribute('src', hoverSrc);
        img.setAttribute('alt', '');
        img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';
        fo.appendChild(img);
      }

      // Insert BEFORE the paths so media is behind the lines
      liveSvg.insertBefore(fo, defs.nextSibling);
      mediaFoEl = fo;
    }

    // Dynamically create small square <rect> elements at each vertex.
    var boxEls = [];
    var boxCount = Math.min(vertsA.length, vertsB.length);
    var boxStroke = '#363638';
    var boxStrokeHover = '#FFFFFF';
    var boxFill = '#000000';
    var boxFillHover = '#FFFFFF';
    for (var v = 0; v < boxCount; v++) {
      var rect = document.createElementNS(SVG_NS, 'rect');
      rect.setAttribute('width', BOX_SIZE);
      rect.setAttribute('height', BOX_SIZE);
      rect.setAttribute('x', vertsA[v].x - BOX_HALF);
      rect.setAttribute('y', vertsA[v].y - BOX_HALF);
      // rect.setAttribute('fill', 'white');
      rect.setAttribute('fill', boxFill);
      // rect.setAttribute('opacity', '0.3');
      rect.setAttribute('opacity', '1');
      rect.setAttribute('stroke', boxStroke);
      rect.setAttribute('stroke-width', '1');
      liveSvg.appendChild(rect);
      boxEls.push({
        el: rect,
        ax: vertsA[v].x - BOX_HALF,
        ay: vertsA[v].y - BOX_HALF,
        bx: vertsB[v].x - BOX_HALF,
        by: vertsB[v].y - BOX_HALF
      });
    }

    // Add abbreviation text label at the topmost vertex square.
    // The closing ] sits 4px to the left of the topmost rect's left edge.
    var abbr = wrapper.getAttribute('data-abbreviation');
    var abbrEl = null;
    if (abbr && vertsA.length > 0) {
      var topIdx = 0;
      for (var t = 1; t < vertsA.length; t++) {
        if (vertsA[t].y < vertsA[topIdx].y) topIdx = t;
      }

      // mark top box so its colour can be changed on hover
      boxEls[topIdx].isTop = true;

      var axText = vertsA[topIdx].x - BOX_HALF - 4;
      var ayText = vertsA[topIdx].y;
      var aFill = '#8b8f94';
      var bxText = topIdx < vertsB.length ? vertsB[topIdx].x - BOX_HALF - 4 : axText;
      var byText = topIdx < vertsB.length ? vertsB[topIdx].y : ayText;
      var bFill = '#FFFFFF';

      var textEl = document.createElementNS(SVG_NS, 'text');
      textEl.setAttribute('x', axText);
      textEl.setAttribute('y', ayText);
      textEl.setAttribute('text-anchor', 'end');
      textEl.setAttribute('dominant-baseline', 'middle');
      textEl.setAttribute('fill', aFill);
      textEl.setAttribute('font-size', '0.75rem');
      textEl.setAttribute('font-family', 'IBM Plex Mono, monospace');
      textEl.setAttribute('letter-spacing', '0.5');
      textEl.textContent = abbr;
      liveSvg.appendChild(textEl);

      abbrEl = { el: textEl, ax: axText, ay: ayText, aFill: aFill, bx: bxText, by: byText, bFill: bFill };
    }

    wrapper.appendChild(liveSvg);

    // Hide source containers now that we have the live SVG.
    srcA.style.display = 'none';
    srcB.style.display = 'none';

    // Collect live paths and their B-targets (d strings).
    var livePaths = liveSvg.querySelectorAll('path');
    var pairs = [];
    var count = Math.min(livePaths.length, pathsB.length);

    for (var i = 0; i < count; i++) {
      pairs.push({
        el: livePaths[i],
        dA: livePaths[i].getAttribute('d'),
        dB: pathsB[i].getAttribute('d')
      });
    }

    var morphTl = null;
    var pointsA = vertsToPoints(vertsA);
    var pointsB = vertsToPoints(vertsB);

    if (window.matchMedia('(hover: hover)').matches) {
      card.addEventListener('mouseenter', function() {
        if (morphTl) { morphTl.kill(); morphTl = null; }
        pairs.forEach(function(p) { gsap.killTweensOf(p.el); });
        boxEls.forEach(function(b) { gsap.killTweensOf(b.el); });
        if (abbrEl) gsap.killTweensOf(abbrEl.el);
        if (clipPolygon) gsap.killTweensOf(clipPolygon);
        if (mediaFoEl) gsap.killTweensOf(mediaFoEl);

        morphTl = gsap.timeline();

        // Morph all line paths
        pairs.forEach(function(p) {
          morphTl.to(p.el, {
            morphSVG: p.dB,
            duration: 0.6,
            ease: 'power2.out'
          }, 0);
        });

        // Animate boxes to B positions
        boxEls.forEach(function(b) {
          let stroke = b.isTop ? boxStrokeHover : boxStroke;
          let fill = b.isTop ? boxFillHover : boxFill;
          morphTl.to(b.el, {
            attr: { x: b.bx, y: b.by, stroke: stroke, fill: fill },
            duration: 0.6,
            ease: 'power2.out'
          }, 0);
        });

        // Animate abbreviation text to B position
        if (abbrEl) {
          morphTl.to(abbrEl.el, {
            attr: { x: abbrEl.bx, y: abbrEl.by, fill: abbrEl.bFill },
            duration: 0.6,
            ease: 'power2.out'
          }, 0);
        }

        // Animate clip polygon to B shape + fade in media
        if (clipPolygon) {
          morphTl.to(clipPolygon, {
            attr: { points: pointsB },
            duration: 0.6,
            ease: 'power2.out'
          }, 0);
        }
        if (mediaFoEl) {
          // Start video if present
          var vid = mediaFoEl.querySelector('video');
          if (vid) vid.play();
          morphTl.to(mediaFoEl, {
            opacity: 1,
            duration: 0.4,
            ease: 'power2.out'
          }, 0);
        }
      });

      card.addEventListener('mouseleave', function() {
        if (morphTl) { morphTl.kill(); morphTl = null; }
        pairs.forEach(function(p) { gsap.killTweensOf(p.el); });
        boxEls.forEach(function(b) { gsap.killTweensOf(b.el); });
        if (abbrEl) gsap.killTweensOf(abbrEl.el);
        if (clipPolygon) gsap.killTweensOf(clipPolygon);
        if (mediaFoEl) gsap.killTweensOf(mediaFoEl);

        morphTl = gsap.timeline();

        // Morph all line paths back
        pairs.forEach(function(p) {
          morphTl.to(p.el, {
            morphSVG: p.dA,
            duration: 0.6,
            ease: 'power2.inOut'
          }, 0);
        });

        // Animate boxes back to A positions
        boxEls.forEach(function(b) {
          morphTl.to(b.el, {
            attr: { x: b.ax, y: b.ay, stroke: boxStroke, fill: boxFill },
            duration: 0.6,
            ease: 'power2.inOut'
          }, 0);
        });

        // Animate abbreviation text back to A position
        if (abbrEl) {
          morphTl.to(abbrEl.el, {
            attr: { x: abbrEl.ax, y: abbrEl.ay, fill: abbrEl.aFill },
            duration: 0.6,
            ease: 'power2.inOut'
          }, 0);
        }

        // Animate clip polygon back to A + fade out media
        if (clipPolygon) {
          morphTl.to(clipPolygon, {
            attr: { points: pointsA },
            duration: 0.6,
            ease: 'power2.inOut'
          }, 0);
        }
        if (mediaFoEl) {
          morphTl.to(mediaFoEl, {
            opacity: 0,
            duration: 0.3,
            ease: 'power2.in',
            onComplete: function() {
              var vid = mediaFoEl.querySelector('video');
              if (vid) vid.pause();
            }
          }, 0);
        }
      });
    }
  });
}
